import {
    EpcEnergyEfficiencyRating,
    EstateSubType,
    EstateType,
    Job,
    JobRole,
    Language,
    NonOwnerObjective,
    OwnerObjective,
    PhoneNumberValidationMethod,
    Platform,
    Portal,
    PropertyCondition,
    PropertyMainUsage,
    RegionCode,
    SaleMethod,
    Salutation,
    ViewFromProperty,
} from '../enums';

export interface NewEstimationRequest extends Record<string, any> {
    // We extend Record<string, any> to be able to validate this object directly
    readonly portal: Portal;
    readonly platform: Platform;
    readonly keepPreviousPropertyCharacteristics: false;

    readonly cityName: string;
    readonly houseNumber?: string;
    readonly postalCode: string;
    readonly streetName: string;

    // geo reference ids
    // TODO: remove remove VEGA-1163
    readonly addressId?: string;
    readonly streetId?: string;
    readonly cityId?: string;

    readonly geoPlaceId?: string;

    readonly estateType: EstateType;

    readonly estateSubType: EstateSubType;
    readonly livingSurface: number;
    readonly numberOfRooms?: number;
    readonly numberOfBathRooms: number;
    readonly numberOfBedRooms?: number;
    readonly floorNumber?: number;
    readonly numberOfFloorsInBuilding: number;
    readonly totalSiteSurface?: number;

    readonly hasAnElevator?: boolean;
    readonly hasAPrivateGarden?: boolean;
    readonly privateGardensSurface?: number;
    readonly hasABalconyOrTerrace: boolean;
    readonly balconiesAndTerracesSurface?: number;
    readonly hasACellar?: boolean;
    readonly hasABasement?: boolean;
    readonly basementsSurface?: number;
    readonly hasAParking?: boolean;
    readonly numberOfParkingLots?: number;
    // TODO: VEGA-779 hasAnIndoorParking should be mandatory when switch is made in the front-end
    readonly hasAnIndoorParking?: boolean;
    readonly numberOfIndoorParkingLots?: number;
    // TODO: VEGA-779 hasAnOutdoorParking should be mandatory when switch is made in the front-end
    readonly hasAnOutdoorParking?: boolean;
    readonly numberOfOutdoorParkingLots?: number;
    readonly viewFromProperty: ViewFromProperty;
    readonly numberOfFacades?: number;
    // This field is obsolete, not asked anymore in frontend form
    readonly hasAnySharedPartBeenRenovatedRecently?: boolean;
    readonly hasASwimmingPool?: boolean;

    readonly constructionYear?: number;
    readonly constructionPeriodMin?: number;
    readonly constructionPeriodMax?: number;
    readonly averageCondition: PropertyCondition;
    readonly epcEnergyEfficiencyRating?: EpcEnergyEfficiencyRating;
    readonly monthlyCondominiumFee?: number;

    readonly isAPrivatePerson: boolean;
    readonly isPropertyOwner?: boolean;
    readonly propertyMainUsage?: PropertyMainUsage;
    readonly ownerObjective?: OwnerObjective;
    readonly nonOwnerObjective?: NonOwnerObjective;
    readonly saleMethod?: SaleMethod;
    readonly job?: Job;
    readonly jobRole?: JobRole;

    readonly salutation: Salutation;
    readonly firstName: string;
    readonly lastName: string;
    readonly phoneNumberRegionCode: RegionCode;
    readonly phoneNumber: string;
    readonly email: string;
    readonly hasAcceptedEmailMarketingCommunications?: boolean;
    readonly language: Language;
    readonly portalUserId?: string;
    readonly avivUserId?: string;
    readonly hasAcceptedTermsAndConditions: boolean;

    // Phone number validation
    readonly phoneNumberValidationMethod: PhoneNumberValidationMethod;
    readonly phoneNumberVerificationCode?: string;
    readonly phoneNumberVerificationRequestId?: string;

    // Handle Estima
    readonly estimationId?: string;
}

export function calculateRelatedField(
    newEstimationRequest: NewEstimationRequest,
): NewEstimationRequest {
    // Here we need to set hasAParking to true if there is Indoor or Outdoor parking
    let hasAParking = newEstimationRequest.hasAParking;
    if (
        newEstimationRequest.hasAnIndoorParking !== undefined ||
        newEstimationRequest.hasAnOutdoorParking !== undefined
    ) {
        hasAParking =
            newEstimationRequest.hasAnIndoorParking || newEstimationRequest.hasAnOutdoorParking;
    } else if (newEstimationRequest.hasAParking === undefined) {
        hasAParking = false;
    }

    // We also need to sum numberOfIndoorParkingLots and numberOfOutdoorParkingLots
    // (if they exist) in the property numberOfParkingLots
    const numberOfParkingLots = (): number | undefined => {
        if (newEstimationRequest.numberOfParkingLots !== undefined) {
            return newEstimationRequest.numberOfParkingLots;
        }

        if (
            newEstimationRequest.numberOfIndoorParkingLots &&
            newEstimationRequest.numberOfOutdoorParkingLots === undefined
        ) {
            return newEstimationRequest.numberOfIndoorParkingLots;
        }
        if (
            newEstimationRequest.numberOfOutdoorParkingLots &&
            newEstimationRequest.numberOfIndoorParkingLots === undefined
        ) {
            return newEstimationRequest.numberOfOutdoorParkingLots;
        }
        if (
            newEstimationRequest.numberOfIndoorParkingLots &&
            newEstimationRequest.numberOfOutdoorParkingLots
        ) {
            return (
                newEstimationRequest.numberOfIndoorParkingLots +
                newEstimationRequest.numberOfOutdoorParkingLots
            );
        }
        return undefined;
    };
    return {
        ...newEstimationRequest,
        hasAParking,
        numberOfParkingLots: numberOfParkingLots(),
    };
}
